import { Th, Box } from '@chakra-ui/react';
import { useRef, useCallback } from 'react';
import type { TableColumnHeaderProps } from '@chakra-ui/react';
import type { ReactNode } from 'react';

interface ResizableThProps extends TableColumnHeaderProps {
    columnKey: string;
    storedWidth?: number;
    onResize: (columnKey: string, width: number) => void;
    minWidth?: number;
    children?: ReactNode;
}

const ResizableTh = ({
    columnKey,
    storedWidth,
    onResize,
    minWidth = 80,
    children,
    ...thProps
}: ResizableThProps) => {
    const thRef = useRef<HTMLTableCellElement>(null);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const th = thRef.current;
        if (!th) return;

        // Capture ALL sibling <th> widths (preceding AND following) so that
        // every column has an explicit stored width.  With table-layout:fixed
        // this ensures the table grows beyond the container when the total
        // exceeds the available space, enabling horizontal scroll.
        const tr = th.parentElement;
        if (tr) {
            const allThs = Array.from(
                tr.querySelectorAll<HTMLTableCellElement>('th[data-column-key]')
            );
            for (const sibling of allThs) {
                if (sibling === th) continue; // skip self — handled by the drag
                const siblingKey = sibling.getAttribute('data-column-key');
                if (siblingKey) {
                    onResize(siblingKey, Math.round(sibling.getBoundingClientRect().width));
                }
            }
        }

        const startX = e.clientX;
        const startWidth = th.getBoundingClientRect().width;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(minWidth, startWidth + (moveEvent.clientX - startX));
            onResize(columnKey, Math.round(newWidth));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [columnKey, minWidth, onResize]);

    // Resolve the effective CSS min-width: use the larger of our minWidth prop
    // and any explicit minW from thProps (e.g. minW="140px").
    const thMinW = thProps.minW;
    const parsedThMinW =
        typeof thMinW === 'number'
            ? thMinW
            : typeof thMinW === 'string'
                ? parseInt(thMinW, 10) || 0
                : 0;
    const effectiveMin = Math.max(minWidth, parsedThMinW);

    return (
        <Th
            ref={thRef}
            data-column-key={columnKey}
            {...thProps}
            w={storedWidth ? `${storedWidth}px` : thProps.w}
            minW={`${effectiveMin}px`}
            position="relative"
            overflow="visible"
        >
            {children}
            <Box
                position="absolute"
                right="-3px"
                top={0}
                bottom={0}
                w="7px"
                cursor="col-resize"
                onMouseDown={handleMouseDown}
                zIndex={2}
                sx={{
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '2px',
                        top: 0,
                        width: '3px',
                        height: '100%',
                        backgroundColor: '#0071E3',
                        borderRadius: '2px',
                        opacity: 0,
                        transition: 'opacity 0.15s',
                        pointerEvents: 'none',
                    },
                    '&:hover::after': {
                        opacity: 1,
                    },
                }}
            />
        </Th>
    );
};

export default ResizableTh;
